import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import {
  Box,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {Trans, useTranslation} from 'react-i18next';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import './NumberingSorting.scss';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {ReactComponent as RadioActiveIcon} from '../../../../assets/images/radio-active-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../assets/images/radio-enable-icon.svg';
import {ReactComponent as DeleteIcon} from '../../../../assets/images/delete.svg';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ISelectItem} from '../../../../shared/types';
import {
  FormMode,
  getEncodingOptions,
  getKeepDocsOptions,
  getLevelOptions,
  getSortTypeOptions,
  ProductionStatus,
} from '../CreateNewProductionUtils';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrButton from '../../../common/arr-button/ArrButton';
import {
  useGetBatesNext,
  useGetNumberingSortingView,
  useGetTemplateNumberingSortingDetails,
  useSaveNumberingSorting,
  useUploadFile,
} from '../../../../hooks/productions/useProductions';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrCheckbox from '../../../common/arr-checkbox/ArrCheckbox';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import ArrTreeItemSelector, {
  SelectedItemsData,
  TabItem,
} from '../../../common/arr-tree-item-selector/ArrTreeItemSelector';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  GetTemplateDetailsRequestParam,
  GetNumberingSortingViewResponse,
  GetTemplateNumberingSortingDetailsResponse,
  NextBatesNumber,
  SortOrderOption,
} from '../../../../api/types/production.type';
import {getFileName} from '../../../../utils/utils';
import {
  NumberingSortingFormData,
  numberingSortingSchema,
} from '../../../../schemas/productions/productionSchema';
import {
  transformTagTreeData,
  findNodeById,
} from '../CreateNewProductionHelpers';
import {ToastType, FormAction} from '../CreateNewProductionUtils';
import {setIsFieldsChanged} from '../../../../redux/slice/productionSlice';

// Type for child component ref
export interface StepChildRef {
  save: () => void;
  next: () => void;
}

interface NumberingSortingProps {
  productionId: number;
  exportProductionId?: number;
  productionName: string;
  isProductionSet: boolean;
  getStatus?: (showToast: boolean, status: ToastType, message?: string) => void;
  goNext?: () => void;
  goBack?: () => void;
  onStateChange?: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
  productionStatusCode?: string;
  isUpdateNeeded?: boolean;
  onUpdateNeededChange?: (value: boolean) => void;
}

const NumberingSorting = forwardRef<StepChildRef, NumberingSortingProps>(
  (
    {
      productionId,
      exportProductionId = 0,
      productionName,
      getStatus,
      goNext,
      goBack: _goBack,
      onStateChange,
      isProductionSet,
      mode = FormMode.CREATE,
      isTemplate = false,
      templateId,
      productionStatusCode = null,
      isUpdateNeeded = false,
      onUpdateNeededChange,
    },
    ref
  ) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const isInProgressOrCompleted =
      productionStatusCode === ProductionStatus.COMPLETED ||
      productionStatusCode == ProductionStatus.INPROGRESS;
    const modeVal = isInProgressOrCompleted ? FormMode.VIEW : mode;
    const isViewMode = modeVal === FormMode.VIEW;
    const numberingSortingFormRef = useRef<HTMLElement | null>(null);
    const viewModeFocusRingTargetsRef = useRef<
      Array<{
        element: HTMLElement;
        focusHandler: EventListener;
        blurHandler: EventListener;
      }>
    >([]);
    const getNumberingSortingInfoIconA11yProps = (label: string) => ({
      role: 'button' as const,
      tabIndex: 0,
      'aria-label': label,
    });

    const getNumberingSortingRadioInputA11yProps = (
      disabled: boolean,
      checked: boolean
    ): React.InputHTMLAttributes<HTMLInputElement> => ({
      'aria-disabled': disabled,
      'aria-checked': checked,
      tabIndex: disabled ? -1 : undefined,
    });

    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const isInitializingRef = useRef<boolean>(false);
    const initializedSelectedTagItemsRef = useRef<
      TreeViewBaseItem<ExtendedTreeItemProps>[]
    >([]);
    const [selectedLevel, setSelectedLevel] = useState<number>(0);
    const [selectedEncoding, setSelectedEncoding] = useState<number>(0);
    const [metadataOptions, setMetadataOptions] = useState<ISelectItem[]>([]);
    const [selectedMetadata, setSelectedMetadata] =
      useState<ISelectItem | null>(null);
    const [selectedSortType, setSelectedSortType] = useState<number>(0);

    const [sortByMetadata, setSortByMetadata] = useState<ISelectItem | null>(
      null
    );
    const [sortOrderMetadata, setSortOrderMetadata] =
      useState<SortOrderOption | null>(null);
    const [sortOrderOptions, setSortOrderOptions] = useState<SortOrderOption[]>(
      []
    );
    const [subSortByMetadata, setSubSortByMetadata] =
      useState<ISelectItem | null>(null);
    const [subSortOrderMetadata, setSubSortOrderMetadata] =
      useState<SortOrderOption | null>(null);
    const [keepFamiliesTogether, setKeepFamiliesTogether] =
      useState<boolean>(false);
    const [keepDocsSelected, setKeepDocsSelected] =
      useState<ISelectItem | null>(null);
    const [customSortFile, setCustomSortFile] = useState<File | null>(null);
    const [customSortFileName, setCustomSortFileName] = useState<string>('');
    const [customSortFileUploaded, setCustomSortFileUploaded] =
      useState<boolean>(false);
    const [showSelectBatesModal, setShowSelectBatesModal] =
      useState<boolean>(false);
    const [isSinglepageTextTIFFPDF, setIsSinglepageTextTIFFPDF] =
      useState<boolean>(false);

    const [sortSelectedTagData, setSortSelectedTagData] = useState<TabItem[]>(
      []
    );
    const [componentIsDirty, setComponentIsDirty] = useState<boolean>(false);
    const [selectedTagItems, setSelectedTagItems] = useState<
      TreeViewBaseItem<ExtendedTreeItemProps>[]
    >([]);

    const handleTagsItemChange = (data: SelectedItemsData) => {
      /* istanbul ignore next -- view mode guard exercised elsewhere */
      if (isViewMode) return;
      setSelectedTagItems(data.items);
      // Only mark as dirty if not during initialization AND items actually changed from initialized state
      /* istanbul ignore next -- init ref timing depends on nested timeouts */
      if (!isInitializingRef.current) {
        // Compare current items with initialized items to avoid false positives
        const currentIds = data.items
          .map((item) => String(item.id))
          .sort()
          .join(',');
        const initializedIds = initializedSelectedTagItemsRef.current
          .map((item) => String(item.id))
          .sort()
          .join(',');
        /* istanbul ignore next -- timing-sensitive diff; covered by higher-level save flows */
        if (currentIds !== initializedIds) {
          setComponentIsDirty(true);
        }
      }
    };

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const levelOptions: ISelectItem[] = getLevelOptions();
    const encodingOptions: ISelectItem[] = isProductionSet
      ? getEncodingOptions()
      : getEncodingOptions().filter((item) => item.value === 0);
    const sortTypeOptions: ISelectItem[] = getSortTypeOptions();
    const keepDocsOptions: ISelectItem[] = getKeepDocsOptions();

    const {
      register: _register,
      handleSubmit,
      setValue,
      formState: {errors, isSubmitting, isDirty, isValid},
      reset: _reset,
      trigger,
    } = useForm<NumberingSortingFormData>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: zodResolver(numberingSortingSchema()),
      defaultValues: {
        action: FormAction.SAVE,
        subBatesNum: '',
        subBatesMinNumLength: '',
        specificPrefix: '',
        specificSuffix: '',
        fieldPrefix: '',
        fieldSuffix: '',
        selectedLevel: 0,
        isSinglepageTextTIFFPDF: false,
      },
    });

    const isSaveDisabled = isSubmitting || (isSaved && !isDirty) || !isValid;
    const isNextDisabled = isSubmitting || !isValid;

    useEffect(() => {
      if (isDirty || componentIsDirty) {
        dispatch(setIsFieldsChanged({isFieldsChanged: true}));
      } else {
        dispatch(setIsFieldsChanged({isFieldsChanged: false}));
      }
    }, [isDirty, componentIsDirty, dispatch]);

    useEffect(() => {
      if (!isViewMode) {
        return;
      }

      let animationFrameId = 0;

      const getControlText = (element: HTMLElement): string =>
        (element.getAttribute('aria-label') || element.textContent || '')
          .trim()
          .replace(/\s+/g, ' ');

      const setViewModeFocusable = (
        element: HTMLElement | null | undefined,
        ariaLabel?: string
      ) => {
        if (!element) {
          return;
        }

        if (element.getAttribute('tabindex') !== '0') {
          element.setAttribute('tabindex', '0');
        }

        if (element.getAttribute('aria-disabled') !== 'true') {
          element.setAttribute('aria-disabled', 'true');
        }

        if (ariaLabel && element.getAttribute('aria-label') !== ariaLabel) {
          element.setAttribute('aria-label', ariaLabel);
        }

        const hasExistingFocusHandler =
          viewModeFocusRingTargetsRef.current.some(
            (entry) => entry.element === element
          );

        if (!hasExistingFocusHandler) {
          const focusHandler = () => {
            element.style.outline = '2px solid #fff';
            element.style.outlineOffset = '2px';
            element.style.borderRadius = element.style.borderRadius || '4px';
          };
          const blurHandler = () => {
            element.style.outline = '';
            element.style.outlineOffset = '';
          };

          element.addEventListener('focus', focusHandler);
          element.addEventListener('blur', blurHandler);
          viewModeFocusRingTargetsRef.current.push({
            element,
            focusHandler,
            blurHandler,
          });
        }
      };

      const setViewModeNonFocusable = (
        element: HTMLElement | null | undefined
      ) => {
        if (!element) {
          return;
        }

        if (element.getAttribute('tabindex') !== '-1') {
          element.setAttribute('tabindex', '-1');
        }
      };

      const getDropdownLabelFromSelectedTooltip = (
        selectedTooltipElement: HTMLElement
      ): string => {
        const selectedText = getControlText(selectedTooltipElement);
        const dropdownDisplayElement =
          selectedTooltipElement.closest<HTMLElement>(
            '[role="combobox"][data-testid]'
          );
        const dropdownId = dropdownDisplayElement?.getAttribute('data-testid');

        switch (dropdownId) {
          case 'asd-numbering-metadataOptions':
            return `${t(
              'productionsAndExports.createNewProduction.numberingAndSorting.metaData'
            )} ${selectedText}`;
          case 'asd-numbering-sortByMetadata':
            return `${t(
              'productionsAndExports.createNewProduction.numberingAndSorting.sortBy'
            )} ${selectedText}`;
          case 'asd-numbering-sortOrderMetadata':
          case 'asd-numbering-subSortOrderMetadata':
            return `${t(
              'productionsAndExports.createNewProduction.numberingAndSorting.sortOrder'
            )} ${selectedText}`;
          case 'asd-numbering-subSortByMetadata':
            return `${t(
              'productionsAndExports.createNewProduction.numberingAndSorting.subSortBy'
            )} ${selectedText}`;
          case 'asd-numbering-keepDocsSelected':
            return `${t(
              'productionsAndExports.createNewProduction.numberingAndSorting.keepDocs'
            )} ${selectedText}`;
          default:
            return selectedText;
        }
      };

      const applyViewModeFocus = () => {
        const container = numberingSortingFormRef.current;
        if (!container) {
          return;
        }

        const focusableElements = container.querySelectorAll<HTMLElement>(
          'button, input, select, textarea, a[href], [role="button"], [role="radio"], [role="checkbox"], [role="combobox"], [contenteditable="true"], [tabindex]:not([tabindex="-1"])'
        );

        focusableElements.forEach((element) => {
          const isInfoIcon =
            element.classList.contains('info-icon') &&
            element.getAttribute('role') === 'button';
          const isSelectedDropdownValue = element.matches(
            '[data-testid^="selected-item-tooltip-"]'
          );

          if (isInfoIcon) {
            setViewModeFocusable(element);
            return;
          }

          if (isSelectedDropdownValue) {
            setViewModeFocusable(
              element,
              getDropdownLabelFromSelectedTooltip(element)
            );
            return;
          }

          setViewModeNonFocusable(element);
        });

        container
          .querySelectorAll<
            HTMLInputElement | HTMLTextAreaElement
          >('input:not([type="hidden"]), textarea')
          .forEach((inputElement) => {
            const inputType = inputElement.getAttribute('type');
            if (
              inputType === 'radio' ||
              inputType === 'checkbox' ||
              inputType === 'file'
            ) {
              return;
            }

            const hasValue = String(inputElement.value ?? '').trim().length > 0;
            if (!hasValue) {
              return;
            }

            const inputFocusTarget =
              inputElement.closest<HTMLElement>('.MuiFormControl-root') ??
              inputElement.closest<HTMLElement>('.MuiInputBase-root') ??
              inputElement.closest<HTMLElement>('[data-testid]') ??
              inputElement.parentElement ??
              inputElement;

            setViewModeFocusable(
              inputFocusTarget,
              inputElement.getAttribute('aria-label') ?? undefined
            );
          });

        container
          .querySelectorAll<HTMLInputElement>('input[type="radio"]')
          .forEach((radioElement) => {
            if (!radioElement.checked) {
              return;
            }

            const radioFocusTarget =
              radioElement.closest<HTMLElement>('label') ??
              radioElement.closest<HTMLElement>('[data-testid]') ??
              radioElement;

            setViewModeFocusable(
              radioFocusTarget,
              getControlText(radioFocusTarget)
            );
          });

        container
          .querySelectorAll<HTMLInputElement>('input[type="checkbox"]')
          .forEach((checkboxElement) => {
            if (!checkboxElement.checked) {
              return;
            }

            const checkboxFocusTarget =
              checkboxElement.closest<HTMLElement>('label') ??
              checkboxElement.closest<HTMLElement>('[data-testid]') ??
              checkboxElement;

            setViewModeFocusable(
              checkboxFocusTarget,
              getControlText(checkboxFocusTarget)
            );
          });

        if (customSortFileName) {
          const customSortFileNameElement =
            container.querySelector<HTMLElement>(
              '[data-testid="upload-file-name"]'
            );
          setViewModeFocusable(customSortFileNameElement, customSortFileName);
        }
      };

      const scheduleFocusUpdate = () => {
        window.cancelAnimationFrame(animationFrameId);
        animationFrameId = window.requestAnimationFrame(applyViewModeFocus);
      };

      scheduleFocusUpdate();

      const observer = new MutationObserver(scheduleFocusUpdate);
      if (numberingSortingFormRef.current) {
        observer.observe(numberingSortingFormRef.current, {
          subtree: true,
          childList: true,
          attributes: true,
          attributeFilter: ['tabindex', 'disabled', 'aria-disabled', 'value'],
        });
      }

      return () => {
        window.cancelAnimationFrame(animationFrameId);
        observer.disconnect();
        viewModeFocusRingTargetsRef.current.forEach(
          ({element, focusHandler, blurHandler}) => {
            element.removeEventListener('focus', focusHandler);
            element.removeEventListener('blur', blurHandler);
            element.style.outline = '';
            element.style.outlineOffset = '';
          }
        );
        viewModeFocusRingTargetsRef.current = [];
      };
    }, [
      isViewMode,
      selectedLevel,
      selectedEncoding,
      selectedMetadata,
      selectedSortType,
      sortByMetadata,
      sortOrderMetadata,
      subSortByMetadata,
      subSortOrderMetadata,
      sortSelectedTagData,
      keepFamiliesTogether,
      keepDocsSelected,
      customSortFileName,
      t,
    ]);

    // Submit handlers
    const onValid = (data: NumberingSortingFormData) => {
      if (selectedSortType === 2 && !customSortFile) {
        getStatus?.(
          true,
          ToastType.Error,
          t(
            'productionsAndExports.createNewProduction.numberingAndSorting.errors.customFileError'
          )
        );
        return;
      }

      let payload = {
        productionnumberingSettings: {
          subBatesNo: data.subBatesNum === '' ? 0 : Number(data.subBatesNum),
          subBatesNoMinNoLength: Number(data.subBatesMinNumLength),
          eNumberingLevel: selectedLevel,
          isSpecificBatesNo: true,
          eFormatType: selectedEncoding,
          // will check in future for eSpecifyNumberingTypes
          eSpecifyNumberingTypes: 0,
          specificBeginningNo: Number(data.specificBeginningNo),
          specificPrefix: data.specificPrefix,
          specificSuffix: data.specificSuffix,
          specificMinNoLength: Number(data.specificMinNoLength),
          fieldID: selectedMetadata?.metadataFieldID,
          fieldPrefix: data.fieldPrefix,
          fieldSuffix: data.fieldSuffix,
          // Need To check in future
          // hasNumberPerDocument: true,
          // shouldContinuePreviousNo: false,
          // isSinglepageTextTIFFPDF: true,
          // isOnlyNativeSelected: false,
        },
        productionSortingSettings: {
          file: `C:\\fakepath\\${customSortFileName}`,
          sortByFieldID: sortByMetadata?.metadataFieldID,
          isSortByFieldASC: sortOrderMetadata?.value === 'Ascending',
          subSortByFieldID: subSortByMetadata?.metadataFieldID,
          isSubSortByFieldASC: subSortOrderMetadata?.value === 'Ascending',
          sortByIsKeepFamiliesTogether: keepFamiliesTogether,
          eKeepDocsWithNoMasterDate: keepDocsSelected?.value,
          isSortByField: selectedSortType === 0,
          isSortByTagOrder: selectedSortType === 1,
          isSortByCustom: selectedSortType === 2,
          eSortOption: selectedSortType,
          sortTagOrder:
            selectedTagItems && selectedTagItems.length > 0
              ? selectedTagItems.map((item) => item.id).join(',')
              : null,
          // Need To check in future
          // selectedTagList: null,
          // shouldKeepInBegining: false,
          // eSortType: 1,
        },
        isMarkComplete: false,
        productionId,
        productionSecurityGroupId: selectedSecurityGroupId,
      };

      if (data.action === FormAction.SAVE) {
        saveNumberingSorting(payload, {
          onSuccess: (responseData) => {
            getStatus?.(true, ToastType.Success, responseData.message);
            setIsSaved(true);
            // Update parent state to track that update is needed after save
            onUpdateNeededChange?.(true);
            // Reset form with current values to clear dirty state
            _reset(data, {
              keepDefaultValues: false,
              keepDirty: false,
              keepIsValid: true,
            });
            // Reset componentIsDirty after successful save
            setComponentIsDirty(false);
          },
          onError: (error) => {
            getStatus?.(true, ToastType.Error, error?.message);
          },
        });
      }

      if (data.action === FormAction.NEXT) {
        if (!isUpdateNeeded) {
          goNext?.();
          return;
        }

        payload = {
          ...payload,
          isMarkComplete: true,
        };
        saveNumberingSorting(payload, {
          onSuccess: () => {
            getStatus?.(
              true,
              ToastType.Success,
              t(
                'productionsAndExports.createNewProduction.common.nextSuccessToast'
              )
            );
            goNext?.();
          },
          onError: (error) => {
            getStatus?.(true, ToastType.Error, error?.message);
          },
        });
      }
      dispatch(setIsFieldsChanged({isFieldsChanged: false}));
    };

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      save: () => {
        setValue('action', FormAction.SAVE);
        handleSubmit(onValid)();
      },
      next: () => {
        if (isInProgressOrCompleted) {
          goNext?.();
          return;
        }
        setValue('action', FormAction.NEXT);
        handleSubmit(onValid)();
      },
    }));

    // API call to get Numbering component data

    const {securityGroupId: selectedSecurityGroupId} = useSelector(
      (state: RootState) => state.production
    );

    const isNumberingSortingViewEnabled =
      !!selectedSecurityGroupId && productionId > 0 && !isTemplate;
    const isTemplateNumberingSortingViewEnabled =
      !!selectedSecurityGroupId && isTemplate && !!templateId;

    const {
      data: numberingSortingDataFromProduction,
      isFetching: isFetchingNumberingSortingFromProduction,
    } = useGetNumberingSortingView(
      {
        SecurityGroupId: selectedSecurityGroupId,
        productionId: productionId,
      },
      isNumberingSortingViewEnabled
    );

    const {
      data: numberingSortingDataFromTemplate,
      isFetching: isFetchingNumberingSortingFromTemplate,
    } = useGetTemplateNumberingSortingDetails(
      {
        templateId: templateId!,
        securityGroupId: selectedSecurityGroupId,
      } as GetTemplateDetailsRequestParam,
      isTemplateNumberingSortingViewEnabled
    );

    const isNumberingSortingDataFetching = isTemplate
      ? isFetchingNumberingSortingFromTemplate
      : isFetchingNumberingSortingFromProduction;

    // Map template response to production response structure
    const mapTemplateToProduction = (
      templateData: GetTemplateNumberingSortingDetailsResponse
    ): GetNumberingSortingViewResponse => {
      return {
        sortOrder: templateData.sortOrder.map((item) => {
          return {
            ...item,
            value: item.text,
          };
        }),
        sortKeepDocsWithNoMasterDate: templateData.sortKeepDocsWithNoMasterDate,
        lsttags: templateData.tagTreeList,
        productionStatusCode:
          templateData.productionNumberingAndSortingData.productionStatusCode,
        productionNumberingAndSortingModel: {
          productionnumberingSettings: {
            subBatesNo:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.subBatesNo,
            subBatesNoMinNoLength:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.subBatesNoMinNoLength,
            hasNumberPerDocument:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.hasNumberPerDocument,
            eNumberingLevel:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.eNumberingLevel,
            isSpecificBatesNo:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.isSpecificBatesNo,
            eFormatType:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.eFormatType,
            shouldContinuePreviousNo:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.shouldContinuePreviousNo,
            eSpecifyNumberingTypes: 0,
            specificBeginningNo:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.specificBeginningNo,
            specificPrefix:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.specificPrefix,
            specificSuffix:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.specificSuffix,
            specificMinNoLength:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.specificMinNoLength,
            fieldID:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.fieldID,
            fieldPrefix:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.fieldPrefix,
            fieldSuffix:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.fieldSuffix,
            isSinglepageTextTIFFPDF:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.isSinglepageTextTIFFPDF,
            isOnlyNativeSelected:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.isOnlyNativeSelected,
            numberingMetaDataList:
              templateData.productionNumberingAndSortingData
                .productionnumberingSettings.numberingMetaDataList,
          },
          productionSortingSettings: {
            file:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.customSortFilePath || '',
            isSubSortByFieldASC:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.isSubSortByFieldASC,
            sortByFieldID:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.sortByFieldID,
            subSortByFieldID:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.subSortByFieldID,
            sortByIsKeepFamiliesTogether:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.sortByIsKeepFamiliesTogether,
            selectedTagList:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.selectedTagList,
            shouldKeepInBegining:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.shouldKeepInBegining,
            eKeepDocsWithNoMasterDate: 0, // Not in template response, default to 0
            isSortByFieldASC:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.isSortByFieldASC,
            eSortType: 0, // Not in template response, default to 0
            isSortByField:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.isSortByField,
            isSortByTagOrder:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.isSortByTagOrder,
            isSortByCustom:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.isSortByCustom,
            eSortOption: templateData.productionNumberingAndSortingData
              .productionSortingSettings.isSortByField
              ? 0
              : templateData.productionNumberingAndSortingData
                    .productionSortingSettings.isSortByTagOrder
                ? 1
                : templateData.productionNumberingAndSortingData
                      .productionSortingSettings.isSortByCustom
                  ? 2
                  : 0,
            sortTagOrder:
              templateData.productionNumberingAndSortingData
                .productionSortingSettings.sortTagOrder,
          },
          isMarkComplete:
            templateData.productionNumberingAndSortingData.isMarkComplete,
          productionId:
            templateData.productionNumberingAndSortingData.productionId,
          productionSecurityGroupId:
            templateData.productionNumberingAndSortingData
              .productionSecurityGroupId,
        },
      };
    };

    // Use the appropriate data based on isTemplate flag - memoize to prevent infinite loops
    const numberingSortingData: GetNumberingSortingViewResponse | undefined =
      useMemo(() => {
        if (isTemplate && numberingSortingDataFromTemplate) {
          try {
            const mapped = mapTemplateToProduction(
              numberingSortingDataFromTemplate
            );
            return mapped;
          } catch (error) {
            console.error('Error mapping template data:', error);
            return undefined;
          }
        }
        return numberingSortingDataFromProduction;
      }, [
        isTemplate,
        isProductionSet,
        numberingSortingDataFromTemplate,
        numberingSortingDataFromProduction,
      ]);

    // Show a full-section loader until the form has actually been populated.
    const isNumberingSortingDataLoading = !isInitialized;

    const {data: batesNextData} = useGetBatesNext(
      {
        SecurityGroupId: selectedSecurityGroupId,
        productionId: productionId,
      },
      !!selectedSecurityGroupId && productionId > 0 && showSelectBatesModal
    );

    // Report state changes to parent
    useEffect(() => {
      onStateChange?.({
        saveDisabled: isSaveDisabled,
        nextDisabled: isNextDisabled,
      });
    }, [isSaveDisabled, isNextDisabled, onStateChange]);

    // Reset initialization flag when productionId, mode, or the template
    useEffect(() => {
      setIsInitialized(false);
      setComponentIsDirty(false);
    }, [productionId, mode, templateId, isTemplate]);

    useEffect(() => {
      if (
        numberingSortingData?.productionNumberingAndSortingModel &&
        numberingSortingData.productionNumberingAndSortingModel
          .productionnumberingSettings &&
        !isInitialized &&
        !isNumberingSortingDataFetching
      ) {
        isInitializingRef.current = true;
        _reset(
          {
            action: FormAction.SAVE,
            subBatesNum:
              numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.subBatesNo?.toString() ||
              '',
            subBatesMinNumLength:
              numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.subBatesNoMinNoLength?.toString() ||
              '',
            specificBeginningNo:
              numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.specificBeginningNo?.toString() ||
              '',
            specificMinNoLength:
              numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.specificMinNoLength?.toString() ||
              '',
            specificPrefix:
              numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.specificPrefix?.toString() ||
              '',
            specificSuffix:
              numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.specificSuffix?.toString() ||
              '',
            fieldPrefix:
              numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.fieldPrefix?.toString() ||
              '',
            fieldSuffix:
              numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.fieldSuffix?.toString() ||
              '',
            selectedLevel: isProductionSet
              ? numberingSortingData?.productionNumberingAndSortingModel
                  ?.productionnumberingSettings?.eNumberingLevel || 0
              : 1,
            isSinglepageTextTIFFPDF:
              numberingSortingData?.productionNumberingAndSortingModel
                ?.productionnumberingSettings?.isSinglepageTextTIFFPDF || false,
          },
          {keepDefaultValues: false, keepDirty: false, keepIsValid: false}
        );

        setIsSinglepageTextTIFFPDF(
          numberingSortingData?.productionNumberingAndSortingModel
            ?.productionnumberingSettings?.isSinglepageTextTIFFPDF || false
        );

        if (!isProductionSet && exportProductionId > 0) {
          setSelectedLevel(
            numberingSortingData?.productionNumberingAndSortingModel
              ?.productionnumberingSettings?.eNumberingLevel
          );
        } else {
          setSelectedLevel(
            isProductionSet
              ? numberingSortingData?.productionNumberingAndSortingModel
                  ?.productionnumberingSettings?.eNumberingLevel
              : 1
          );
        }

        if (
          numberingSortingData?.productionNumberingAndSortingModel
            ?.productionnumberingSettings?.eNumberingLevel === 0 &&
          isProductionSet
        ) {
          setSelectedEncoding(1);
        } else if (!isProductionSet && exportProductionId > 0) {
          setSelectedEncoding(
            numberingSortingData?.productionNumberingAndSortingModel
              ?.productionnumberingSettings?.eFormatType
          );
        } else {
          setSelectedEncoding(
            isProductionSet
              ? numberingSortingData?.productionNumberingAndSortingModel
                  ?.productionnumberingSettings?.eFormatType
              : 0
          );
        }

        setMetadataOptions(
          (
            numberingSortingData?.productionNumberingAndSortingModel
              ?.productionnumberingSettings?.numberingMetaDataList || []
          ).map((item) => ({
            metadataFieldID: item.metadataFieldID,
            metadataFieldLabel: item.metadataFieldLabel,
          }))
        );

        const selectedMetadataApi =
          numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.numberingMetaDataList?.find(
            (item: any) => {
              if (
                numberingSortingData?.productionNumberingAndSortingModel
                  ?.productionnumberingSettings?.fieldID === 0
              ) {
                return item.metadataFieldLabel === 'AllCustodians';
              } else {
                return (
                  item.metadataFieldID ===
                  numberingSortingData?.productionNumberingAndSortingModel
                    ?.productionnumberingSettings?.fieldID
                );
              }
            }
          );
        setSelectedMetadata(
          selectedMetadataApi
            ? {
                metadataFieldID: selectedMetadataApi.metadataFieldID,
                metadataFieldLabel: selectedMetadataApi.metadataFieldLabel,
              }
            : null
        );

        // sorting
        setSortOrderOptions(numberingSortingData?.sortOrder || []);
        const defaultSortOrder = numberingSortingData?.sortOrder?.find(
          (item: any) => item.isSelected
        );
        setSortOrderMetadata(defaultSortOrder || null);
        setSubSortOrderMetadata(defaultSortOrder || null);
        const treeId = 'sorting-tags';
        const treeData = numberingSortingData?.lsttags
          ? transformTagTreeData(numberingSortingData?.lsttags as any, treeId)
          : [];

        const sortTagOrder =
          numberingSortingData?.productionNumberingAndSortingModel
            ?.productionSortingSettings?.sortTagOrder;
        const selectedTagData = sortTagOrder
          ?.split(',')
          ?.map((id: any) => {
            const tagId = String(id).trim();
            return numberingSortingData?.lsttags.find(
              (tag: any) => String(tag.id) == tagId
            );
          })
          .filter((tag): tag is NonNullable<typeof tag> => Boolean(tag));

        // Initialize selectedTagItems from API data by finding corresponding tree items
        const selectedTagItemsFromApi: TreeViewBaseItem<ExtendedTreeItemProps>[] =
          [];
        if (
          selectedTagData &&
          selectedTagData.length > 0 &&
          treeData.length > 0
        ) {
          selectedTagData.forEach((tag) => {
            const treeItem = findNodeById(treeData, String(tag.id));
            if (treeItem) {
              selectedTagItemsFromApi.push(treeItem);
            }
          });
        }
        setSelectedTagItems(selectedTagItemsFromApi);
        // Store initialized items for comparison later
        initializedSelectedTagItemsRef.current = selectedTagItemsFromApi;

        const tagsData: TabItem[] = [
          {
            tabTitle: 'Available Tags',
            groups: [
              {
                groupTitle: '',
                prefix: '',
                preselectedItemIdField: 'id',
                treeItemIdField: 'id',
                treeId: treeId,
                treeData: treeData,
                preSelectedItems: (selectedTagData ?? []) as any,
              },
            ],
          },
        ];
        setSortSelectedTagData(tagsData);
        if (
          numberingSortingData?.productionNumberingAndSortingModel
            ?.productionSortingSettings
        ) {
          setSelectedSortType(
            numberingSortingData?.productionNumberingAndSortingModel
              ?.productionSortingSettings?.eSortOption
          );

          const sortBy =
            numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.numberingMetaDataList?.find(
              (item: any) => {
                if (
                  numberingSortingData?.productionNumberingAndSortingModel
                    ?.productionSortingSettings?.sortByFieldID === 0
                ) {
                  return item.metadataFieldLabel === 'AllCustodians';
                } else {
                  return (
                    item.metadataFieldID ===
                    numberingSortingData?.productionNumberingAndSortingModel
                      ?.productionSortingSettings?.sortByFieldID
                  );
                }
              }
            );
          setSortByMetadata(
            sortBy
              ? {
                  metadataFieldID: sortBy.metadataFieldID,
                  metadataFieldLabel: sortBy.metadataFieldLabel,
                }
              : null
          );
          const sortOrderSelected = numberingSortingData?.sortOrder?.find(
            (item: any) =>
              (item.value === 'Ascending' &&
                numberingSortingData?.productionNumberingAndSortingModel
                  ?.productionSortingSettings?.isSortByFieldASC) ||
              (item.value === 'Descending' &&
                !numberingSortingData?.productionNumberingAndSortingModel
                  ?.productionSortingSettings?.isSortByFieldASC)
          );
          setSortOrderMetadata(sortOrderSelected ?? defaultSortOrder ?? null);

          const subSortBy =
            numberingSortingData?.productionNumberingAndSortingModel?.productionnumberingSettings?.numberingMetaDataList?.find(
              (item: any) => {
                if (
                  numberingSortingData?.productionNumberingAndSortingModel
                    ?.productionSortingSettings?.subSortByFieldID === 0
                ) {
                  return item.metadataFieldLabel === 'AllCustodians';
                } else {
                  return (
                    item.metadataFieldID ===
                    numberingSortingData?.productionNumberingAndSortingModel
                      ?.productionSortingSettings?.subSortByFieldID
                  );
                }
              }
            );
          setSubSortByMetadata(
            subSortBy
              ? {
                  metadataFieldID: subSortBy.metadataFieldID,
                  metadataFieldLabel: subSortBy.metadataFieldLabel,
                }
              : null
          );
          const subSortOrderSelected = numberingSortingData?.sortOrder?.find(
            (item: any) =>
              (item.value === 'Ascending' &&
                numberingSortingData?.productionNumberingAndSortingModel
                  ?.productionSortingSettings?.isSubSortByFieldASC) ||
              (item.value === 'Descending' &&
                !numberingSortingData?.productionNumberingAndSortingModel
                  ?.productionSortingSettings?.isSubSortByFieldASC)
          );
          setSubSortOrderMetadata(
            subSortOrderSelected ?? defaultSortOrder ?? null
          );
          setKeepFamiliesTogether(
            numberingSortingData?.productionNumberingAndSortingModel
              ?.productionSortingSettings?.sortByIsKeepFamiliesTogether
          );

          const selectedKeepDocsOption = keepDocsOptions.find(
            (item: ISelectItem) =>
              item.value ===
              Number(
                numberingSortingData?.productionNumberingAndSortingModel
                  ?.productionSortingSettings?.shouldKeepInBegining
              )
          );
          setKeepDocsSelected(selectedKeepDocsOption ?? null);
          const fileName = getFileName(
            numberingSortingData?.productionNumberingAndSortingModel
              ?.productionSortingSettings?.file
          );
          setCustomSortFileName(fileName);
        }
        // Set isSaved to true after form is loaded with data to disable save button initially
        setIsSaved(true);
        setIsInitialized(true);
        // Reset componentIsDirty after initialization completes
        setComponentIsDirty(false);
        // Trigger validation after reset to ensure isValid is updated
        // Use setTimeout to ensure validation runs after form state is fully updated
        setTimeout(() => {
          trigger();
          // Clear initialization flag after a short delay to allow ArrTreeItemSelector to initialize
          setTimeout(() => {
            isInitializingRef.current = false;
          }, 100);
        }, 0);
      }
    }, [
      numberingSortingData,
      _reset,
      isProductionSet,
      trigger,
      isInitialized,
      isNumberingSortingDataFetching,
    ]);

    useEffect(() => {
      customSortFile && validationHandler(customSortFile);
    }, [customSortFile]);

    useEffect(() => {
      if (
        selectedEncoding === 0 &&
        !selectedMetadata &&
        metadataOptions.length > 0
      ) {
        setSelectedMetadata(metadataOptions[0]);
      }
    }, [selectedEncoding, selectedMetadata, metadataOptions]);

    useEffect(() => {
      if (
        selectedSortType === 0 &&
        !sortByMetadata &&
        metadataOptions.length > 0
      ) {
        setSortByMetadata(metadataOptions[0]);
      }
      if (
        selectedSortType === 0 &&
        !subSortByMetadata &&
        metadataOptions.length > 0
      ) {
        setSubSortByMetadata(metadataOptions[0]);
      }
    }, [selectedSortType, sortByMetadata, subSortByMetadata, metadataOptions]);

    /**
     * Vadidation Handler for file
     * @param file
     */
    const validationHandler = (file: File) => {
      const maxAllowedSize: number = 10 * 1024 * 1024;
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      const isInvalidFileType = !allowedTypes.includes(file.type);

      if (Number(file?.size) > maxAllowedSize) {
        getStatus?.(
          true,
          ToastType.Error,
          t(
            'productionsAndExports.createNewProduction.numberingAndSorting.largeFileError'
          )
        );
      } else if (isInvalidFileType) {
        getStatus?.(
          true,
          ToastType.Error,
          t(
            'productionsAndExports.createNewProduction.numberingAndSorting.unSupportedFile'
          )
        );
      } else {
        uploadCustomSortFile(file, {
          onSuccess: (data) => {
            setCustomSortFileUploaded(true);
            setCustomSortFileName(file.name);
            getStatus?.(true, ToastType.Success, data?.message);
          },
          onError: (error) => {
            setCustomSortFileName('');
            setCustomSortFileUploaded(false);
            getStatus?.(true, ToastType.Error, error?.message);
          },
        });
      }
    };

    const handleUploadClick = () => {
      if (isViewMode) return;
      fileInputRef.current?.click();
      setCustomSortFile(null);
      setComponentIsDirty(true);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isViewMode) return;
      const files = e.target.files;

      if (files) {
        setCustomSortFile(files[0]);
        e.target.value = '';
      }
      setComponentIsDirty(true);
    };

    const {mutate: uploadCustomSortFile} = useUploadFile();
    const {mutate: saveNumberingSorting} = useSaveNumberingSorting();

    const handleBatesSelect = (batesSelect: NextBatesNumber) => {
      if (isViewMode) return;
      if (batesSelect) {
        setValue('specificBeginningNo', batesSelect.beginBates, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue('specificMinNoLength', batesSelect.minLength, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue('specificPrefix', batesSelect.prefix, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue('specificSuffix', batesSelect.suffix, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setShowSelectBatesModal(false);
        setComponentIsDirty(true);
      }
    };

    if (isNumberingSortingDataLoading) {
      return (
        <Box className="numbering-sorting-form-section">
          <Box className="numbering-sorting-loading-container">
            <ArrLoadingSpinner />
          </Box>
        </Box>
      );
    }

    return (
      <Box
        className="numbering-sorting-form-section"
        component="form"
        onSubmit={handleSubmit(onValid)}
        data-testid="numbering-sorting-form"
        ref={numberingSortingFormRef}
      >
        <Box className="numbering-sorting-container">
          <Box className="numbering-sorting-header">
            <Typography
              id="numbering-header"
              className="title-text"
              data-testid={`numbering-sorting-numbering-title`}
            >
              {t(
                'productionsAndExports.createNewProduction.numberingAndSorting.numberingTitle'
              )}
            </Typography>
          </Box>
          <Box
            className="numbering-sorting-body"
            aria-labelledby="numbering-body"
          >
            <Box className="level-container">
              <Box className="level-label-container">
                <Typography className="level-text" data-testid="level-text">
                  {t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.levelText'
                  )}
                </Typography>
                <ArrTooltipWrapper
                  closeTooltipOnEscPress
                  title={t(
                    `productionsAndExports.createNewProduction.numberingAndSorting.${isTemplate ? 'templateLevelInfoTitle' : 'levelInfoTitle'}`
                  )}
                  trigger={
                    <InfoIcon
                      className="info-icon"
                      {...getNumberingSortingInfoIconA11yProps('Level info')}
                    />
                  }
                  dialogClassName="number-sorting"
                >
                  <Trans
                    i18nKey="productionsAndExports.createNewProduction.numberingAndSorting.levelInfo"
                    components={{
                      p: <p data-testid={`numbering-sorting-p`} />,
                    }}
                  />
                </ArrTooltipWrapper>
              </Box>
              <RadioGroup
                row
                role="radiogroup"
                value={selectedLevel}
                onChange={(e) => {
                  if (isViewMode) return;
                  setComponentIsDirty(true);
                  const val = Number(e.target.value);
                  setSelectedLevel(val);
                  setValue('selectedLevel', val, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  if (val === 0) {
                    setSelectedEncoding(1);
                  }
                }}
                className="custom-radio-group"
                aria-label={t(
                  'productionsAndExports.createNewProduction.numberingAndSorting.'
                )}
              >
                {levelOptions.map(({value, label}, index) => {
                  return (
                    <FormControlLabel
                      data-testid={`level-Options-${value}`}
                      key={value}
                      value={value}
                      tabIndex={isViewMode ? -1 : 0}
                      onKeyDown={(e) => {
                        if (isViewMode) return;
                        if (e.key === 'Enter') {
                          e.preventDefault();

                          const val = Number(value);
                          setSelectedLevel(val);
                          setValue('selectedLevel', val, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }
                      }}
                      control={
                        <Radio
                          disabled={
                            isViewMode ||
                            (!isProductionSet && value !== selectedLevel)
                          }
                          icon={<RadioEnableIcon />}
                          checkedIcon={<RadioActiveIcon />}
                          className="custom-radio-label"
                          slotProps={{
                            input: {
                              'data-testid': `radio-level-${value}`,
                              ...getNumberingSortingRadioInputA11yProps(
                                isViewMode ||
                                  (selectedLevel === 0 && value === 0),
                                Number(value) === selectedEncoding
                              ),
                              ...getNumberingSortingRadioInputA11yProps(
                                isViewMode ||
                                  (!isProductionSet && value !== selectedLevel),
                                Number(value) === selectedLevel
                              ),
                            } as React.InputHTMLAttributes<HTMLInputElement>,
                          }}
                        />
                      }
                      className="custom-radio-label"
                      label={
                        <span
                          className="radio-text"
                          data-testid={`numbering-sorting-label-${index}`}
                        >
                          {label}
                        </span>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </Box>
            {selectedLevel === 1 && (
              <Box className="document-grid-container">
                <Box className="document-sub-container">
                  <Typography
                    id="document-sub-bates"
                    className="document-sub-bates-text"
                    data-testid={`numbering-sorting-sub-bates-num`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.numberingAndSorting.subBatesNum'
                    )}
                  </Typography>
                  <ArrTextField
                    id="subBatesNum"
                    placeholder={
                      isSinglepageTextTIFFPDF
                        ? t(
                            'productionsAndExports.createNewProduction.numberingAndSorting.subBatesNumPlaceHolderRequired'
                          )
                        : t(
                            'productionsAndExports.createNewProduction.numberingAndSorting.subBatesNumPlaceHolder'
                          )
                    }
                    {..._register('subBatesNum')}
                    state={
                      isViewMode
                        ? 'inactive'
                        : errors.subBatesNum
                          ? 'error'
                          : 'enabled'
                    }
                    supportText={errors.subBatesNum?.message}
                    sizeVariant="medium"
                    slotProps={{
                      input: {
                        'aria-label': t(
                          'productionsAndExports.createNewProduction.numberingAndSorting.subBatesNum'
                        ),
                        'aria-labelledby': 'sub-bates-num',
                        'aria-invalid': !!errors.subBatesNum,
                        'aria-disabled': isViewMode,
                        tabIndex: isViewMode ? -1 : undefined,
                      },
                    }}
                    dataTestId={'subBatesNum'}
                  />
                </Box>
                <Box className="document-sub-container">
                  <Typography
                    id="document-sub-bates-min-num-length"
                    className="document-sub-bates-text"
                    data-testid={`numbering-sorting-min-num-length`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.numberingAndSorting.minNumLength'
                    )}
                  </Typography>
                  <ArrTextField
                    id="subBatesMinNumLength"
                    {..._register('subBatesMinNumLength')}
                    state={
                      isViewMode
                        ? 'inactive'
                        : errors.subBatesMinNumLength
                          ? 'error'
                          : 'enabled'
                    }
                    supportText={errors.subBatesMinNumLength?.message}
                    sizeVariant="medium"
                    slotProps={{
                      input: {
                        'aria-label': t(
                          'productionsAndExports.createNewProduction.numberingAndSorting.minNumLength'
                        ),
                        'aria-labelledby': 'sub-bates-min-num-length',
                        'aria-invalid': !!errors.subBatesMinNumLength,
                        'aria-disabled': isViewMode,
                        tabIndex: isViewMode ? -1 : undefined,
                      },
                    }}
                    dataTestId={'subBatesMinNumLength'}
                  />
                </Box>
              </Box>
            )}
            <Divider orientation="horizontal" className="divider" />
            <Box className="level-container encoding-container">
              <Box className="level-label-container">
                <Typography className="level-text" data-testid="level-text">
                  {t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.encoding'
                  )}
                </Typography>
                {!isTemplate && (
                  <ArrTooltipWrapper
                    closeTooltipOnEscPress
                    title={t(
                      'productionsAndExports.createNewProduction.numberingAndSorting.encoding'
                    )}
                    trigger={
                      <InfoIcon
                        className="info-icon"
                        {...getNumberingSortingInfoIconA11yProps(
                          'Encoding info'
                        )}
                      />
                    }
                    dialogClassName="number-sorting"
                  >
                    <Trans
                      i18nKey="productionsAndExports.createNewProduction.numberingAndSorting.encoding"
                      components={{
                        p: <p data-testid={`numbering-sorting-p-text`} />,
                      }}
                    />
                  </ArrTooltipWrapper>
                )}
              </Box>
              <RadioGroup
                row
                role="radiogroup"
                value={selectedEncoding}
                onChange={(e) => {
                  if (isViewMode) return;
                  setSelectedEncoding(Number(e.target.value));
                  setComponentIsDirty(true);
                }}
                className="custom-radio-group"
              >
                {encodingOptions.map(({value, label, info}, index) => {
                  return (
                    <FormControlLabel
                      data-testid={`encoding-Options-custom-group`}
                      tabIndex={isViewMode ? -1 : 0}
                      onKeyDown={(e) => {
                        if (isViewMode) return;
                        if (e.key === 'Enter') {
                          e.preventDefault();

                          setSelectedEncoding(Number(value));
                          setComponentIsDirty(true);
                        }
                      }}
                      key={value}
                      value={value}
                      control={
                        <Radio
                          disabled={
                            isViewMode || (selectedLevel === 0 && value === 0)
                          }
                          icon={<RadioEnableIcon />}
                          checkedIcon={<RadioActiveIcon />}
                          className="custom-radio-label"
                          slotProps={{
                            input: {
                              'data-testid': `radio-level-${value}`,
                            } as React.InputHTMLAttributes<HTMLInputElement>,
                          }}
                        />
                      }
                      className="custom-radio-label"
                      label={
                        <Box className="encoding-options-text">
                          <Typography
                            className="radio-text"
                            data-testid={`encoding-radio-text-${index}`}
                          >
                            {label}
                          </Typography>
                          <ArrTooltipWrapper
                            closeTooltipOnEscPress
                            title={label}
                            trigger={
                              <InfoIcon
                                className="info-icon"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                data-testid="numbering-sorting-info-icon"
                                {...getNumberingSortingInfoIconA11yProps(
                                  'Specify Bates Numbering info'
                                )}
                              />
                            }
                            dialogClassName="number-sorting"
                          >
                            <Trans
                              i18nKey={String(info)}
                              components={{
                                p: (
                                  <p data-testid={`numbering-sorting-p-btn`} />
                                ),
                                b: <b data-testid={`numbering-sorting-b`} />,
                              }}
                            />
                          </ArrTooltipWrapper>
                        </Box>
                      }
                    />
                  );
                })}
              </RadioGroup>
              {selectedEncoding === 1 && isProductionSet && (
                <ArrButton
                  variant="primary"
                  size="small"
                  onClick={() => {
                    if (isViewMode) return;
                    setShowSelectBatesModal(true);
                    setComponentIsDirty(true);
                  }}
                  aria-label="View or select next Bates numbers"
                  dataTestId="selectBatesBtn"
                  disabled={isViewMode}
                >
                  {t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.selectBatesButton'
                  )}
                </ArrButton>
              )}
              {selectedEncoding === 1 && isProductionSet && (
                <Box className="bates-grid-container">
                  <Box className="bates-sub-container">
                    <Typography
                      id="beginning-bates"
                      className="bates-sub-bates-text"
                      data-testid={`numbering-sorting-beginning-bates`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.beginningBates'
                      )}
                    </Typography>
                    <ArrTextField
                      id="beginning-bates-num"
                      {..._register('specificBeginningNo')}
                      state={
                        isViewMode
                          ? 'inactive'
                          : errors.specificBeginningNo
                            ? 'error'
                            : 'enabled'
                      }
                      supportText={errors.specificBeginningNo?.message}
                      sizeVariant="medium"
                      slotProps={{
                        input: {
                          'aria-label': t(
                            'productionsAndExports.createNewProduction.numberingAndSorting.beginningBates'
                          ),
                          'aria-labelledby': 'beginning-bates-num',
                          'aria-invalid': !!errors.specificBeginningNo,
                          'aria-disabled': isViewMode,
                          tabIndex: isViewMode ? -1 : undefined,
                        },
                      }}
                      dataTestId={'beginning-bates-num'}
                      className="text-input-max"
                    />
                  </Box>
                  <Box className="bates-sub-container">
                    <Typography
                      id="beginning-bates-min-length"
                      className="bates-sub-bates-text"
                      data-testid={`numbering-sorting-min-num-length-text`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.minNumLength'
                      )}
                    </Typography>
                    <ArrTextField
                      id="beginning-bates-min-length-num"
                      {..._register('specificMinNoLength')}
                      state={
                        isViewMode
                          ? 'inactive'
                          : errors.specificMinNoLength
                            ? 'error'
                            : 'enabled'
                      }
                      supportText={errors.specificMinNoLength?.message}
                      sizeVariant="medium"
                      slotProps={{
                        input: {
                          'aria-label': t(
                            'productionsAndExports.createNewProduction.numberingAndSorting.minNumLength'
                          ),
                          'aria-labelledby': 'beginning-bates-min-length-num',
                          'aria-invalid': !!errors.specificMinNoLength,
                          'aria-disabled': isViewMode,
                          tabIndex: isViewMode ? -1 : undefined,
                        },
                      }}
                      dataTestId={'beginning-bates-min-length-num'}
                      className="text-input-max"
                    />
                  </Box>
                  <Box className="bates-sub-container">
                    <Typography
                      id="prefix"
                      className="bates-sub-bates-text"
                      data-testid={`numbering-sorting-prefix`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.prefix'
                      )}
                    </Typography>
                    <ArrTextField
                      id="prefix-val"
                      {..._register('specificPrefix')}
                      state={
                        isViewMode
                          ? 'inactive'
                          : errors.specificPrefix
                            ? 'error'
                            : 'enabled'
                      }
                      supportText={errors.specificPrefix?.message}
                      placeholder={t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.prefix'
                      )}
                      sizeVariant="medium"
                      slotProps={{
                        input: {
                          'aria-label': t(
                            'productionsAndExports.createNewProduction.numberingAndSorting.prefix'
                          ),
                          'aria-labelledby': 'prefix-val',
                          'aria-invalid': !!errors.specificPrefix,
                          'aria-disabled': isViewMode,
                          tabIndex: isViewMode ? -1 : undefined,
                        },
                      }}
                      dataTestId={'prefix-val'}
                      className="text-input-max"
                    />
                  </Box>
                  <Box className="bates-sub-container">
                    <Typography
                      id="suffix"
                      className="bates-sub-bates-text"
                      data-testid={`numbering-sorting-suffix`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.suffix'
                      )}
                    </Typography>
                    <ArrTextField
                      id="suffix-val"
                      {..._register('specificSuffix')}
                      state={
                        isViewMode
                          ? 'inactive'
                          : errors.specificSuffix
                            ? 'error'
                            : 'enabled'
                      }
                      supportText={errors.specificSuffix?.message}
                      placeholder={t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.suffix'
                      )}
                      sizeVariant="medium"
                      slotProps={{
                        input: {
                          'aria-label': t(
                            'productionsAndExports.createNewProduction.numberingAndSorting.suffix'
                          ),
                          'aria-labelledby': 'suffix-val',
                          'aria-invalid': !!errors.specificSuffix,
                          'aria-disabled': isViewMode,
                          tabIndex: isViewMode ? -1 : undefined,
                        },
                      }}
                      dataTestId={'suffix-val'}
                      className="text-input-max"
                    />
                  </Box>
                </Box>
              )}
              {selectedEncoding === 0 && (
                <Box className="metadata-grid-container">
                  <Box className="metadata-sub-container">
                    <Typography
                      id="metadata-id"
                      className="metadata-text"
                      data-testid={`numbering-sorting-meta-data`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.metaData'
                      )}
                    </Typography>
                    <ArrSingleSelectDropdown
                      dropdownLabel={''}
                      dropdownItems={metadataOptions}
                      selectedValues={selectedMetadata}
                      onSelectionChange={(item) => {
                        if (isViewMode) return;
                        item && setSelectedMetadata(item);
                        setComponentIsDirty(true);
                      }}
                      dropdownItemKey="metadataFieldID"
                      dropdownItemDisplay="metadataFieldLabel"
                      sizeVariant="medium"
                      dataTestId="asd-numbering-metadataOptions"
                      customWidth={14}
                      tabIndex={isViewMode ? -1 : undefined}
                      selectRestProps={{
                        'aria-disabled': isViewMode,
                      }}
                      disabled={isViewMode}
                      searchable
                      showNoResultsText
                    />
                  </Box>
                  <Box className="metadata-sub-container">
                    <Typography
                      id="metadata-prefix"
                      className="metadata-sub-text"
                      data-testid={`numbering-sorting-prefix-text`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.prefix'
                      )}
                    </Typography>
                    <ArrTextField
                      id="metadata-prefix-val"
                      {..._register('fieldPrefix')}
                      state={
                        isViewMode
                          ? 'inactive'
                          : errors.fieldPrefix
                            ? 'error'
                            : 'enabled'
                      }
                      supportText={errors.fieldPrefix?.message}
                      placeholder={t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.prefix'
                      )}
                      sizeVariant="medium"
                      slotProps={{
                        input: {
                          'aria-label': t(
                            'productionsAndExports.createNewProduction.numberingAndSorting.prefix'
                          ),
                          'aria-labelledby': 'metadata-prefix-val',
                          'aria-invalid': !!errors.fieldPrefix,
                          'aria-disabled': isViewMode,
                          tabIndex: isViewMode ? -1 : undefined,
                        },
                      }}
                      dataTestId={'metadata-prefix-val'}
                    />
                  </Box>
                  <Box className="metadata-sub-container">
                    <Typography
                      id="metadata-suffix"
                      className="metadata-sub-text"
                      data-testid={`numbering-sorting-suffix-text`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.suffix'
                      )}
                    </Typography>
                    <ArrTextField
                      id="metadata-suffix-val"
                      {..._register('fieldSuffix')}
                      state={
                        isViewMode
                          ? 'inactive'
                          : errors.fieldSuffix
                            ? 'error'
                            : 'enabled'
                      }
                      supportText={errors.fieldSuffix?.message}
                      placeholder={t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.suffix'
                      )}
                      sizeVariant="medium"
                      slotProps={{
                        input: {
                          'aria-label': t(
                            'productionsAndExports.createNewProduction.numberingAndSorting.suffix'
                          ),
                          'aria-labelledby': 'metadata-suffix-val',
                          'aria-invalid': !!errors.fieldSuffix,
                          'aria-disabled': isViewMode,
                          tabIndex: isViewMode ? -1 : undefined,
                        },
                      }}
                      dataTestId={'metadata-suffix-val'}
                    />
                  </Box>
                </Box>
              )}
              {selectedEncoding === 1 && exportProductionId > 0 && (
                <Typography
                  id="export-warring"
                  data-testid={`numbering-sorting-export-number-warring`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.exportNumberWarring'
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box className="numbering-sorting-container">
          <Box className="numbering-sorting-header">
            <Typography
              id="numbering-header"
              className="title-text"
              data-testid={`numbering-sorting-sorting-title`}
            >
              {t(
                'productionsAndExports.createNewProduction.numberingAndSorting.sortingTitle'
              )}
            </Typography>
          </Box>
          <Box
            className="numbering-sorting-body"
            aria-labelledby="sorting-body"
          >
            <Box className="level-container">
              <Box className="level-label-container">
                <Typography className="level-text" data-testid="level-text">
                  {t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.selectSortType'
                  )}
                </Typography>
                <ArrTooltipWrapper
                  closeTooltipOnEscPress
                  title={sortTypeOptions[selectedSortType].infoTitle}
                  trigger={
                    <InfoIcon
                      className="info-icon"
                      {...getNumberingSortingInfoIconA11yProps('Sorting info')}
                    />
                  }
                  dialogClassName="number-sorting"
                >
                  <Trans
                    i18nKey={String(
                      sortTypeOptions[selectedSortType]?.info ?? ''
                    )}
                    components={{
                      p: <p data-testid={`numbering-sorting-p-button`} />,
                      b: <b data-testid={`numbering-sorting-b-btn`} />,
                    }}
                  />
                </ArrTooltipWrapper>
              </Box>
              <RadioGroup
                row
                role="radiogroup"
                value={selectedSortType}
                onChange={(e) => {
                  if (isViewMode) return;
                  const val = Number(e.target.value);
                  setSelectedSortType(val);
                  setComponentIsDirty(true);
                }}
                className="custom-radio-group"
              >
                {sortTypeOptions.map(({value, label}, index) => {
                  return (
                    <FormControlLabel
                      data-testid={`sort-type-options-custom-radio-group`}
                      key={value}
                      value={value}
                      tabIndex={isViewMode ? -1 : 0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();

                          if (isViewMode) return;
                          const val = Number(value);
                          setSelectedSortType(val);
                          setComponentIsDirty(true);
                        }
                      }}
                      control={
                        <Radio
                          icon={<RadioEnableIcon />}
                          checkedIcon={<RadioActiveIcon />}
                          className="custom-radio-label"
                          disabled={isViewMode}
                          slotProps={{
                            input: {
                              'data-testid': `radio-sort-type-${value}`,
                              ...getNumberingSortingRadioInputA11yProps(
                                isViewMode,
                                Number(value) === selectedSortType
                              ),
                            } as React.InputHTMLAttributes<HTMLInputElement>,
                          }}
                        />
                      }
                      className="custom-radio-label"
                      label={
                        <span
                          className="radio-text"
                          data-testid={`numbering-sorting-label-${index}`}
                        >
                          {label}
                        </span>
                      }
                    />
                  );
                })}
              </RadioGroup>
            </Box>

            {selectedSortType === 0 && (
              <Divider orientation="horizontal" className="divider" />
            )}
            {selectedSortType === 0 && (
              <Box className="level-container sorting-container">
                <Box className="sort-grid-container">
                  <Box className="sort-sub-container">
                    <Typography
                      id="sortBy"
                      className="sort-text"
                      data-testid={`numbering-sorting-sort-by`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.sortBy'
                      )}
                    </Typography>
                    <ArrSingleSelectDropdown
                      dropdownLabel={''}
                      dropdownItems={metadataOptions}
                      selectedValues={sortByMetadata}
                      onSelectionChange={(item) => {
                        if (isViewMode) return;
                        item && setSortByMetadata(item);
                        setComponentIsDirty(true);
                      }}
                      dropdownItemKey="metadataFieldID"
                      dropdownItemDisplay="metadataFieldLabel"
                      sizeVariant="medium"
                      dataTestId="asd-numbering-sortByMetadata"
                      customWidth={16.25}
                      tabIndex={isViewMode ? -1 : undefined}
                      selectRestProps={{
                        'aria-disabled': isViewMode,
                      }}
                      disabled={isViewMode}
                      searchable
                      showNoResultsText
                    />
                  </Box>
                  <Box className="sort-sub-container">
                    <Typography
                      id="sortOrder"
                      className="sort-text"
                      data-testid={`numbering-sorting-sort-order`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.sortOrder'
                      )}
                    </Typography>
                    <ArrSingleSelectDropdown
                      dropdownLabel={''}
                      dropdownItems={sortOrderOptions as any}
                      selectedValues={sortOrderMetadata as any}
                      onSelectionChange={(item) => {
                        if (isViewMode) return;
                        item &&
                          setSortOrderMetadata(
                            item as unknown as SortOrderOption
                          );
                        setComponentIsDirty(true);
                      }}
                      dropdownItemKey="key"
                      dropdownItemDisplay="value"
                      sizeVariant="medium"
                      dataTestId="asd-numbering-sortOrderMetadata"
                      customWidth={16.25}
                      tabIndex={isViewMode ? -1 : undefined}
                      selectRestProps={{
                        'aria-disabled': isViewMode,
                      }}
                      disabled={isViewMode}
                    />
                  </Box>
                  <Box className="sort-sub-container">
                    <Typography
                      id="subSortBy"
                      className="sort-text"
                      data-testid={`numbering-sorting-sub-sort-by`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.subSortBy'
                      )}
                    </Typography>
                    <ArrSingleSelectDropdown
                      dropdownLabel={''}
                      dropdownItems={metadataOptions}
                      selectedValues={subSortByMetadata}
                      onSelectionChange={(item) => {
                        if (isViewMode) return;
                        item && setSubSortByMetadata(item);
                        setComponentIsDirty(true);
                      }}
                      dropdownItemKey="metadataFieldID"
                      dropdownItemDisplay="metadataFieldLabel"
                      sizeVariant="medium"
                      dataTestId="asd-numbering-subSortByMetadata"
                      customWidth={16.25}
                      tabIndex={isViewMode ? -1 : undefined}
                      selectRestProps={{
                        'aria-disabled': isViewMode,
                      }}
                      disabled={isViewMode}
                      searchable
                      showNoResultsText
                    />
                  </Box>
                  <Box className="sort-sub-container">
                    <Typography
                      id="subSortOrder"
                      className="sort-text"
                      data-testid={`numbering-sorting-sort-order-text`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.sortOrder'
                      )}
                    </Typography>
                    <ArrSingleSelectDropdown
                      dropdownLabel={''}
                      dropdownItems={sortOrderOptions as any}
                      selectedValues={subSortOrderMetadata as any}
                      onSelectionChange={(item) => {
                        if (isViewMode) return;
                        item &&
                          setSubSortOrderMetadata(
                            item as unknown as SortOrderOption
                          );
                        setComponentIsDirty(true);
                      }}
                      dropdownItemKey="key"
                      dropdownItemDisplay="value"
                      sizeVariant="medium"
                      dataTestId="asd-numbering-subSortOrderMetadata"
                      customWidth={16.25}
                      tabIndex={isViewMode ? -1 : undefined}
                      selectRestProps={{
                        'aria-disabled': isViewMode,
                      }}
                      disabled={isViewMode}
                    />
                  </Box>
                </Box>
              </Box>
            )}
            {selectedSortType === 1 && (
              <Box className="select-tags" data-testid={'select-tags'}>
                <ArrTreeItemSelector
                  tabs={sortSelectedTagData}
                  onSelectedItemsChange={handleTagsItemChange}
                  isPreExpanded={true}
                  disabled={isViewMode}
                  availablePanelTitle={t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.availableTags'
                  )}
                  selectedPanelTitle={t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.selectedTags'
                  )}
                  noItemsAvailableText={t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.noItemsAvailableText'
                  )}
                  noFieldsSelectedText={t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.noFieldsSelectedText'
                  )}
                />
              </Box>
            )}
            {(selectedSortType === 0 || selectedSortType === 1) && (
              <Divider orientation="horizontal" className="divider" />
            )}
            {(selectedSortType === 0 || selectedSortType === 1) && (
              <Box className="families-container">
                <ArrCheckbox
                  checked={keepFamiliesTogether}
                  onChange={(checked) => {
                    if (isViewMode) return;
                    setKeepFamiliesTogether(checked);
                    setComponentIsDirty(true);
                  }}
                  labelText={t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.keepFamiliesTogether'
                  )}
                  size="small"
                  id={`keep-families-together`}
                  aria-checked={keepFamiliesTogether}
                  tabIndex={isViewMode ? -1 : undefined}
                  disabled={isViewMode}
                  aria-label={'keep-families-together'}
                />
                <Box className="families-sub-container">
                  <Typography
                    id="keepDocs"
                    className="keep-docs-text"
                    data-testid={`numbering-sorting-keep-docs`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.numberingAndSorting.keepDocs'
                    )}
                  </Typography>
                  <ArrSingleSelectDropdown
                    dropdownLabel={''}
                    dropdownItems={keepDocsOptions}
                    selectedValues={keepDocsSelected}
                    onSelectionChange={(item) => {
                      if (isViewMode) return;
                      item && setKeepDocsSelected(item);
                      setComponentIsDirty(true);
                    }}
                    dropdownItemKey="value"
                    dropdownItemDisplay="label"
                    sizeVariant="medium"
                    dataTestId="asd-numbering-keepDocsSelected"
                    customWidth={16.25}
                    tabIndex={isViewMode ? -1 : undefined}
                    selectRestProps={{
                      'aria-disabled': isViewMode,
                    }}
                    disabled={isViewMode}
                  />
                </Box>
              </Box>
            )}
            {selectedSortType === 2 &&
              (mode === FormMode.VIEW ? (
                <Typography
                  className="sort-helper-text typo-text body3_size"
                  data-testid={`numbering-sorting-sort-helper-text`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.numberingAndSorting.sortHelperText'
                  )}
                </Typography>
              ) : (
                <Box className="custom-sort">
                  {customSortFileUploaded || customSortFileName ? (
                    <Box className="custom-sort-upload">
                      <Typography
                        className="custom-sort-title"
                        data-testid="upload-file"
                      >
                        {t(
                          'productionsAndExports.createNewProduction.numberingAndSorting.customSortTitle'
                        )}
                      </Typography>
                      <Box className="custom-sort-file">
                        <Typography
                          className=" typo-text body2_size custom-sort-file-text"
                          data-testid="upload-file-name"
                        >
                          {customSortFileName}
                        </Typography>
                        <ArrIconButton
                          icon={<DeleteIcon />}
                          onClick={() => {
                            if (isViewMode) return;
                            setCustomSortFileUploaded(false);
                            setCustomSortFile(null);
                            setCustomSortFileName('');
                            setComponentIsDirty(true);
                          }}
                          ariaLabel={'delete'}
                          className="delete-button"
                          size="small"
                          disabled={isViewMode}
                          testId="numbering-sorting-delete-icon-button"
                        />
                      </Box>
                    </Box>
                  ) : (
                    <ArrButton
                      variant="secondary"
                      size="small"
                      onClick={handleUploadClick}
                      dataTestId="customSortUploadBtn"
                      disabled={isViewMode}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.uploadCustomSortBtn'
                      )}
                    </ArrButton>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xls,.xlsx"
                    style={{display: 'none'}}
                    onChange={handleFileSelect}
                    data-testid={`numbering-sorting-input`}
                  />
                </Box>
              ))}
          </Box>
        </Box>
        <ArrModal
          showModal={showSelectBatesModal}
          showClose={true}
          onClose={() => setShowSelectBatesModal(false)}
          title={t(
            'productionsAndExports.createNewProduction.numberingAndSorting.nextBatesTitle'
          )}
          dataTestId="batesModal"
          className="numbering-sorting-form-section__modal-container"
          titleEndIcon={
            <ArrTooltipWrapper
              closeTooltipOnEscPress
              title={t(
                'productionsAndExports.createNewProduction.numberingAndSorting.nextBatesTitle'
              )}
              trigger={
                <InfoIcon
                  className="info-icon"
                  {...getNumberingSortingInfoIconA11yProps('Sort type info')}
                />
              }
              dialogClassName="number-sorting"
            >
              <Trans
                i18nKey="productionsAndExports.createNewProduction.numberingAndSorting.nextBatesTitle"
                components={{
                  p: <p data-testid={`numbering-sorting-p-icon`} />,
                }}
              />
            </ArrTooltipWrapper>
          }
          buttons={[]}
        >
          <Box className="modal-child-container">
            <Box className="child-header">
              <Typography
                className="typo-text label2_size bates-modal-title"
                data-testid="bates-modal-title"
              >
                {t(
                  'productionsAndExports.createNewProduction.numberingAndSorting.productionName'
                )}
              </Typography>
              <Typography
                className="typo-text subhead2_size bates-production-name"
                data-testid="bates-production-name"
              >
                {productionName}
              </Typography>
            </Box>
            <Table className="child-table">
              <TableHead className="table-header">
                <TableRow>
                  <TableCell className="table-cell border-right">
                    {t(
                      'productionsAndExports.createNewProduction.numberingAndSorting.nextBatesTitle1'
                    )}
                  </TableCell>
                  <TableCell className="table-cell">
                    {t(
                      'productionsAndExports.createNewProduction.numberingAndSorting.action'
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batesNextData?.nextBatesNumbers?.length &&
                batesNextData?.nextBatesNumbers?.length > 0 ? (
                  batesNextData?.nextBatesNumbers.map((row, idx) => (
                    <TableRow key={row.batesNumber + idx}>
                      <TableCell className="table-body-cell">
                        {row.batesNumber}
                      </TableCell>
                      <TableCell className="table-body-cell">
                        <ArrButton
                          variant="secondary"
                          size="small"
                          onClick={() => handleBatesSelect(row)}
                          dataTestId={`selectBtn-${idx}`}
                          disabled={isViewMode}
                        >
                          {t(
                            'productionsAndExports.createNewProduction.numberingAndSorting.selectBtn'
                          )}
                        </ArrButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={2}
                      align="center"
                      className="empty-cell"
                    >
                      {t(
                        'productionsAndExports.createNewProduction.numberingAndSorting.emptyMsg'
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </ArrModal>
      </Box>
    );
  }
);

NumberingSorting.displayName = 'NumberingSorting';

export default NumberingSorting;
